import { useState } from 'react'
import { jurisdictionPillLabel } from '../../utils/jurisdiction.js'
import { APP_NAME, APP_TAGLINE } from '../../utils/constants.js'
import { displayNameOf } from '../../utils/profile.js'
import { Nip05Display } from '../Nip05Display.jsx'
import { todaysVoteCount, dailyTargetFor } from '../../utils/quillStreak.js'

// Avatar block for the header chip. Tries profile.picture first; on
// load failure or missing URL, falls back to a colored circle showing
// the first character of the user's display label. referrerPolicy is
// "no-referrer" so external picture hosts can't see the user's npub
// via the Referer header.
function HeaderAvatar({ profile, label }) {
  const [imgFailed, setImgFailed] = useState(false)
  if (profile?.picture && !imgFailed) {
    return (
      <img
        className="header-avatar"
        src={profile.picture}
        alt=""
        referrerPolicy="no-referrer"
        loading="eager"
        onError={() => setImgFailed(true)}
      />
    )
  }
  const initial = (label?.[0] || '?').toUpperCase()
  return (
    <div className="header-avatar-fallback" aria-hidden="true">
      {initial}
    </div>
  )
}

export function Header({
  jurisdiction,
  isLoggedIn,
  locked,
  npub,
  loginType,
  profile,
  nip05Verified, // T16: 'verified' | 'unverified' | 'unknown' from the lifted hook
  quillState,    // UX36a/UX48: { streak, quillFoundOn, dayCounts, ... } | undefined
  onLogin,
  onCreateAccount,
  onLogout, // accepted for back-compat; the chip routes to onAccountClick now
  onUnlock,
  onJurisdictionClick,
  onAccountClick,
}) {
  const pillLabel = jurisdictionPillLabel(jurisdiction)
  const isEphemeral = loginType === 'ephemeral'
  const labelText = isEphemeral
    ? 'Anonymous'
    : displayNameOf(profile, npub) || 'Connected'
  const sourceLabel =
    loginType === 'nip07' ? 'Extension' :
    loginType === 'local-encrypted' ? 'Encrypted key' :
    loginType === 'local' ? 'Local key' :
    loginType === 'google' ? 'Google' :
    isEphemeral ? 'Anonymous — save it to keep across devices' : null
  const chipTitle = isEphemeral
    ? "Anonymous session — your votes count, but they won't travel. Click to save."
    : [labelText, npub, sourceLabel && `(${sourceLabel})`]
        .filter(Boolean)
        .join(' · ')

  return (
    <header className="header">
      <div className="header-inner">
        <div className="brand">
          <img className="brand-logo" src="/favicon.svg" alt="" aria-hidden="true" />
          <div className="brand-text">
            <span className="brand-name">{APP_NAME}</span>
            <span className="brand-tagline">{APP_TAGLINE}</span>
          </div>
        </div>

        <div className="header-actions">
          <button
            type="button"
            className={`jurisdiction-pill ${pillLabel ? '' : 'empty'}`}
            onClick={onJurisdictionClick}
            title="Set your jurisdiction"
          >
            {pillLabel ? `📍 ${pillLabel}` : '📍 Set location'}
          </button>

          {/* UX48 (revised): keep the quill mechanic mysterious. We
              never surface the daily target number — users discover
              the rule by playing. Badge shows the streak alongside the
              quill, with a subtle at-risk variant when today's target
              is still unmet. No pre-quill indicator at all; the first
              "You found a quill!" moment should be a surprise. */}
          {quillState?.quillFoundOn && (() => {
            // UX49: today's target depends on whether a quill is held
            // (keep bar) or being re-earned (find bar). We never
            // display the number — just the safe/at-risk tell.
            const safe = todaysVoteCount(quillState) >= dailyTargetFor(quillState)
            return (
              <span
                className={`header-quill${safe ? ' header-quill-safe' : ' header-quill-at-risk'}`}
                title={safe
                  ? `${quillState.streak}-day streak. Keep going.`
                  : `${quillState.streak}-day streak. Vote more today to hold your quill.`}
                aria-label={safe
                  ? `Civic streak: ${quillState.streak} days.`
                  : `Civic streak: ${quillState.streak} days. Today's target not yet met.`}
              >
                <span aria-hidden="true">🪶</span>
                <span className="header-quill-count">{quillState.streak}</span>
              </span>
            )
          })()}

          {isLoggedIn ? (
            /* UX51: mobile real-estate reclaim. The chip is now just
               the avatar circle — no name, no inline nip05 text.
               Ephemeral users get a straw ring on the chip so the
               unsaved-session cue carries over without text; verified
               NIP-05 users get a tiny ✓ overlay in the corner. The
               full name and verification details live one tap away
               in AccountModal. */
            <button
              type="button"
              className={`account-chip account-chip-icon${isEphemeral ? ' account-chip-ephemeral' : ''}`}
              onClick={onAccountClick || onLogout || (() => {})}
              title={chipTitle}
              aria-label={labelText}
            >
              <HeaderAvatar profile={profile} label={labelText} />
              {!isEphemeral && nip05Verified === 'verified' && (
                <span
                  className="account-chip-verified"
                  aria-hidden="true"
                  title="Verified NIP-05"
                >✓</span>
              )}
            </button>
          ) : locked ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={onUnlock}
              title="Unlock your encrypted account"
            >
              Unlock
            </button>
          ) : (
            // UX30: two side-by-side CTAs so first-time visitors can see
            // the create-account path without clicking Sign in first. The
            // Create button lands on the UX28 Create view directly; Sign
            // in lands on the modal's own default (Extension if NIP-07 is
            // detected, else Create).
            <div className="header-auth-cta">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onCreateAccount || onLogin}
              >
                Create account
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onLogin}
              >
                Sign in
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
